import { ORG_ROUTE_ACCESS } from '@/config/routeAccessRegistry';
import type { PermissionAction } from '@/config/permissionCodes';
import {
  resolvePageAccess,
  resolveRouteAccessRule,
  type PermissionRule,
  type RouteAccessRule,
} from '@/config/routeAccessUtils';
import type { UiScreenActions } from '@/types/ui';

export type ResourcePermissions = {
  canView: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canApprove: boolean;
  canReject: boolean;
  canExport: boolean;
  canImport: boolean;
  canProcess: boolean;
  resolved: boolean;
};

function ruleForAction(rule: RouteAccessRule | undefined, action: PermissionAction): PermissionRule | undefined {
  if (!rule) return undefined;
  switch (action) {
    case 'view':
      return rule.view;
    case 'create':
      return rule.create ?? rule.view;
    case 'edit':
      return rule.edit ?? rule.create ?? rule.view;
    case 'delete':
      return rule.delete ?? rule.edit ?? rule.create ?? rule.view;
    case 'approve':
      return rule.approve ?? rule.edit;
    case 'reject':
      return rule.reject ?? rule.approve ?? rule.edit;
    case 'export':
      return rule.export ?? rule.edit;
    case 'import':
      return rule.import ?? rule.create;
    case 'process':
      return rule.process ?? rule.edit;
    default:
      return rule.view;
  }
}

function backendAllows(action: PermissionAction, screen: UiScreenActions): boolean {
  switch (action) {
    case 'view':
      return screen.canView;
    case 'create':
      return screen.canCreate;
    case 'edit':
      return screen.canEdit;
    case 'delete':
      return screen.canDelete;
    case 'approve':
      return screen.canApprove;
    case 'reject':
      return screen.canReject;
    case 'export':
      return screen.canExport;
    case 'import':
      return screen.canImport;
    case 'process':
      return screen.canProcess;
    default:
      return false;
  }
}

function clientAllows(
  action: PermissionAction,
  pathname: string,
  has: (code: string) => boolean,
): boolean {
  const rule = resolveRouteAccessRule(pathname, ORG_ROUTE_ACCESS);
  const page = resolvePageAccess(rule, has);
  switch (action) {
    case 'view':
      return page.canView;
    case 'create':
      return page.canCreate;
    case 'edit':
      return page.canEdit;
    case 'delete':
      return page.canDelete;
    case 'approve':
      return has('APPROVAL_ACT') || page.canEdit;
    case 'reject':
      return has('APPROVAL_ACT') || page.canEdit;
    case 'export':
      return page.canEdit;
    case 'import':
      return page.canCreate;
    case 'process':
      return page.canEdit;
    default:
      return false;
  }
}

/** Backend screenActions when loaded; otherwise client route registry. */
export function resolveResourcePermissions(
  pathname: string,
  screenActions: UiScreenActions & { resolved?: boolean },
  has: (code: string) => boolean,
): ResourcePermissions {
  const resolved = screenActions.resolved === true;
  const can = (action: PermissionAction) =>
    resolved ? backendAllows(action, screenActions) : clientAllows(action, pathname, has);

  return {
    canView: can('view'),
    canCreate: can('create'),
    canEdit: can('edit'),
    canDelete: can('delete'),
    canApprove: can('approve'),
    canReject: can('reject'),
    canExport: can('export'),
    canImport: can('import'),
    canProcess: can('process'),
    resolved,
  };
}

export function canPerformAction(
  pathname: string,
  action: PermissionAction,
  screenActions: UiScreenActions & { resolved?: boolean },
  has: (code: string) => boolean,
): boolean {
  const perms = resolveResourcePermissions(pathname, screenActions, has);
  switch (action) {
    case 'view':
      return perms.canView;
    case 'create':
      return perms.canCreate;
    case 'edit':
      return perms.canEdit;
    case 'delete':
      return perms.canDelete;
    case 'approve':
      return perms.canApprove;
    case 'reject':
      return perms.canReject;
    case 'export':
      return perms.canExport;
    case 'import':
      return perms.canImport;
    case 'process':
      return perms.canProcess;
    default:
      return false;
  }
}

export function permissionCodesForAction(pathname: string, action: PermissionAction): string[] {
  const rule = resolveRouteAccessRule(pathname, ORG_ROUTE_ACCESS);
  const permissionRule = ruleForAction(rule, action);
  if (permissionRule == null) return [];
  if (Array.isArray(permissionRule)) return permissionRule;
  return [permissionRule];
}
